import React, { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';

import Form from 'Components-redesign/Form';
import { useDispatch, useFormData, useI18n } from 'Hooks';
import { areFormValuesEqual } from 'Scenes-redesign/PIM/utils';
import { getLangText } from 'utils/langText';

import {
  createUpsell as Create,
  createUpsellVariables as CreateVariables,
  updateUpsell as Update,
  UpdateUpsellsMetadata,
  UpdateUpsellsMetadataVariables,
  updateUpsellVariables as UpdateVariables,
} from './__queries__';
import { getUpsellFormFields } from './fields';
import { CREATE_MUTATION, NODE_QUERY, UPDATE_MUTATION, UPDATE_UPSELL_METADATA } from './queries';
import { showQuestionModal } from './utils';
import * as actions from '../../../../actions';
import {
  getItems_getItems_results as Item,
  getCompany_company_upsells as Upsell,
} from '../../__queries__';

interface Props {
  items: Item[];
  upsells: Upsell[];
  idToUpdate?: string;
  onFormSuccess: (node) => void;
  setShowUnsavedChangesQuestionModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

const UpsellForm = ({
  items,
  upsells,
  idToUpdate,
  onFormSuccess,
  setShowUnsavedChangesQuestionModal,
}: Props) => {
  const [create] = useMutation<Create, CreateVariables>(CREATE_MUTATION);
  const [update] = useMutation<Update, UpdateVariables>(UPDATE_MUTATION);
  const [updateMetadata] = useMutation<UpdateUpsellsMetadata, UpdateUpsellsMetadataVariables>(
    UPDATE_UPSELL_METADATA,
  );
  const { t } = useI18n();
  const dispatch = useDispatch();

  const [changes, setChanges] = useState<any>(null);

  const itemsList = useMemo(() => {
    return items.map((item) => ({ ...item, price: item.defaultPrice }));
  }, [items]);

  const defaultFields = getUpsellFormFields(t, idToUpdate, itemsList);

  const [formState, getMutationData] = useFormData(idToUpdate, defaultFields, NODE_QUERY);

  useEffect(() => {
    if (idToUpdate && setShowUnsavedChangesQuestionModal) {
      if (formState && changes && areFormValuesEqual(formState, changes)) {
        setShowUnsavedChangesQuestionModal(false);
      } else {
        setShowUnsavedChangesQuestionModal(true);
      }
    }
  }, [idToUpdate, formState, changes]);

  const updateMetaData = async (node) => {
    // Update the upsell meta data after creating or updating an upsell
    try {
      const res = await updateMetadata({
        variables: {
          input: {
            upsellId: node.id,
            enableMultiSelect: changes.enableMultiSelect.value,
            hideInPOS: changes.hideInPOS.value,
            hideInEPOS: changes.hideInEPOS.value,
            hideInApp: changes.hideInApp.value,
          },
        },
      });

      if (!res) {
        throw new Error('Error updating, please try again');
      }

      onFormSuccess(node);
    } catch (error) {
      dispatch(actions.showNotice('Error', 4000));
    }
  };

  return (
    <Form
      idToUpdate={idToUpdate}
      fields={formState}
      createMutation={create}
      updateMutation={update}
      parseMutationVariables={getMutationData}
      buttonText={idToUpdate ? t('PIM.UpdateUpsell') : t('PIM.CreateUpsell')}
      floatingButton={true}
      onFormSuccess={(node) => updateMetaData(node)}
      getFieldChanges={(values) => setChanges(values)}
      handleQuestionModal={() => showQuestionModal(idToUpdate, upsells, changes)}
      questionBeforeSubmit={`${t('PIM.AlreadyUpsellWithName')} ${getLangText(
        changes?.name?.value,
      )}, ${t('PIM.SureWantToContinue')}`}
    />
  );
};

export default UpsellForm;
